/**
 * The Owner's edit dialog: every field the task has, from every state, with
 * the in-flight and finished cases warned about explicitly.
 */

import type { ReactNode } from 'react';

import type { ApiFailure, TaskView } from '../../api.js';
import { DependencyPicker, MarkdownField, useWorkspaceCopy } from '../../components.js';
import { ChoiceGroup, ConfirmDialog, InlineAlert, TextField } from '../../ui.js';
import { mergeExpectationApplies } from './merge-expectation.js';
import type { TaskDialogFields } from './useTaskDialogFields.js';
import type { DialogKind } from './useTaskDialogs.js';

export function TaskEditDialog({
  view,
  dialog,
  busy,
  failureAlert,
  closeDialog,
  act,
  fields,
  approvableTypes,
  loadDependencyCandidates,
}: {
  readonly view: TaskView;
  readonly dialog: DialogKind;
  readonly busy: boolean;
  readonly actionFailure: ApiFailure | null;
  readonly failureAlert: ReactNode;
  readonly closeDialog: () => void;
  readonly act: (view: TaskView, kind: Exclude<DialogKind, null>) => void;
  readonly fields: TaskDialogFields;
  readonly approvableTypes: readonly string[];
  readonly loadDependencyCandidates: (selfTaskId: string) => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const task = view.task;
  const isBacklog = task.status === 'BACKLOG';
  const {
    requiresMerge,
    setRequiresMerge,
    editType,
    setEditType,
    editDifficulty,
    setEditDifficulty,
    editDescription,
    setEditDescription,
    dependencyIds,
    setDependencyIds,
    dependencyCandidates,
    editTitle,
    setEditTitle,
    editSuggestedPriority,
    setEditSuggestedPriority,
    editPriority,
    setEditPriority,
    editUnassign,
    setEditUnassign,
  } = fields;

  return (
    <>
      {/* The Owner's edit. Every field the task has, from every state —
          with the in-flight and finished cases warned about explicitly,
          because changing the shape of work somebody is doing (or has
          done) is a different act from editing a proposal. */}
      <ConfirmDialog
        open={dialog === 'edit'}
        title={copy.edit.title}
        body={copy.edit.body}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.edit.effects}
        confirmLabel={copy.actions.save}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={() => {
          act(view, 'edit');
        }}
      >
        {task.status === 'IN_PROGRESS' || task.status === 'IN_REVIEW' ? (
          <InlineAlert tone="warning" role="status">
            {copy.edit.activeWarning}
          </InlineAlert>
        ) : null}
        {task.status === 'DONE' ? (
          <InlineAlert tone="warning" role="status">
            {copy.edit.doneWarning}
          </InlineAlert>
        ) : null}

        <TextField
          label={copy.edit.titleLabel}
          value={editTitle}
          onChange={setEditTitle}
          maxLength={200}
        />
        <ChoiceGroup
          legend={copy.propose.typeLabel}
          value={editType}
          onChange={setEditType}
          inline
          choices={approvableTypes.map((value) => ({
            value,
            label: copy.types[value] ?? value,
          }))}
        />
        {/* A type change that would strip the assignee's access needs
            saying out loud before it is allowed to happen. */}
        {editType !== null && editType !== task.taskType && task.assignee !== null ? (
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              font: 'var(--type-body-md)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-md)',
              minHeight: 44,
            }}
          >
            <input
              type="checkbox"
              checked={editUnassign}
              onChange={(event) => {
                setEditUnassign(event.target.checked);
              }}
              style={{ width: 18, height: 18, accentColor: 'var(--mj-teal-600)' }}
            />
            <span>
              {copy.edit.unassignLabel}
              <span
                style={{
                  display: 'block',
                  font: 'var(--type-caption)',
                  color: 'var(--text-muted)',
                }}
              >
                {copy.edit.unassignHint}
              </span>
            </span>
          </label>
        ) : null}
        <ChoiceGroup
          legend={copy.propose.difficultyLabel}
          value={editDifficulty}
          onChange={setEditDifficulty}
          inline
          choices={['EXTRA_SMALL', 'SMALL', 'MEDIUM', 'LARGE', 'EXTRA_LARGE'].map(
            (value) => ({ value, label: copy.difficulties[value] ?? value }),
          )}
        />
        <ChoiceGroup
          legend={copy.propose.suggestedPriorityLabel}
          hint={copy.propose.suggestedPriorityHint}
          value={editSuggestedPriority}
          onChange={setEditSuggestedPriority}
          inline
          choices={['P1', 'P2', 'P3', 'P4'].map((value) => ({
            value,
            label: copy.priorities[value] ?? value,
          }))}
        />
        {/* The approved priority exists only after approval — the database
            says so, and offering the control on a proposal would imply an
            Owner could approve by editing. */}
        {isBacklog ? (
          <p
            style={{
              font: 'var(--type-caption)',
              color: 'var(--text-muted)',
              marginBottom: 'var(--space-md)',
            }}
          >
            {copy.edit.approvedPriorityBacklog}
          </p>
        ) : (
          <ChoiceGroup
            legend={copy.edit.approvedPriorityLabel}
            value={editPriority}
            onChange={setEditPriority}
            inline
            choices={['P1', 'P2', 'P3', 'P4'].map((value) => ({
              value,
              label: copy.priorities[value] ?? value,
            }))}
          />
        )}
        <MarkdownField
          label={copy.propose.descriptionLabel}
          hint={copy.propose.descriptionHint}
          value={editDescription}
          onChange={setEditDescription}
          rows={8}
          maxLength={20000}
        />
        {mergeExpectationApplies(editType ?? task.taskType) ? (
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              font: 'var(--type-body-md)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-md)',
              minHeight: 44,
            }}
          >
            <input
              type="checkbox"
              checked={requiresMerge}
              onChange={(event) => {
                setRequiresMerge(event.target.checked);
              }}
              style={{ width: 18, height: 18, accentColor: 'var(--mj-teal-600)' }}
            />
            {copy.dialogs.requiresMergeLabel}
          </label>
        ) : null}
        <DependencyPicker
          legend={copy.execution.dependencies}
          hint={copy.propose.prerequisitesHint}
          candidates={dependencyCandidates}
          selected={dependencyIds}
          onToggle={(taskId, wanted) => {
            setDependencyIds((current) =>
              wanted
                ? current.includes(taskId)
                  ? current
                  : [...current, taskId]
                : current.filter((id) => id !== taskId),
            );
          }}
          onRetry={() => {
            loadDependencyCandidates(task.id);
          }}
        />
      </ConfirmDialog>
    </>
  );
}
